import { Medication, MedicationSearchResult, MedicineLibraryProductMatch } from '@xmedical/core';

/**
 * One row in the medication picker's search results. The picker shows two sections when the practice has the
 * medicine library on (#333): the practice's own list, then library products. `sectionHeader` is set on the
 * first row of each section and null otherwise; with the library off there are no headers, so the results
 * look exactly as before.
 */
export type MedicationSearchEntry =
  | { kind: 'practice'; medication: Medication; sectionHeader: string | null }
  | { kind: 'library'; product: MedicineLibraryProductMatch; sectionHeader: string | null };

export const PRACTICE_SECTION_HEADER = 'Your medications';
export const LIBRARY_SECTION_HEADER = 'Medicine library';

/** Entries for a v1 search (practice list only, no library): no section headers. */
export function practiceSearchEntries(items: Medication[]): MedicationSearchEntry[] {
  return (items ?? []).map(medication => ({ kind: 'practice', medication, sectionHeader: null }));
}

/** Entries for a v2 search: section headers only when the practice has the library on. */
export function medicationSearchEntries(result: MedicationSearchResult): MedicationSearchEntry[] {
  const practiceItems = result?.practiceItems ?? [];
  if (!result?.libraryEnabled) {
    return practiceSearchEntries(practiceItems);
  }

  const practiceEntries: MedicationSearchEntry[] = practiceItems.map((medication, index) => ({
    kind: 'practice',
    medication,
    sectionHeader: index === 0 ? PRACTICE_SECTION_HEADER : null,
  }));
  const libraryEntries: MedicationSearchEntry[] = (result.libraryProducts ?? []).map((product, index) => ({
    kind: 'library',
    product,
    sectionHeader: index === 0 ? LIBRARY_SECTION_HEADER : null,
  }));
  return [...practiceEntries, ...libraryEntries];
}
